import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { reportService } from '../services/api';
import { isoDate } from '../utils/date';

const COLORS = ['#a3e635', '#10b981', '#38bdf8', '#f59e0b', '#a78bfa', '#ef4444'];
const METHOD = { cash: 'Dinheiro', pix: 'PIX', card_debit: 'Débito', card_credit: 'Crédito', balance: 'Saldo', mercadopago: 'Mercado Pago' };
const KIND = { session: 'Sessões', package: 'Pacotes de horas', product: 'Lanchonete', topup: 'Recargas' };
const iso = isoDate;
const money = v => `R$ ${Number(v || 0).toFixed(2)}`;
const hours = m => `${(Number(m || 0) / 60).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`;

export default function Reports() {
  const today = new Date();
  const [filters, setFilters] = useState({ start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(today) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try { setData((await reportService.getPeriod(filters)).data); }
    catch (err) { setError(err.response?.data?.error || 'Não foi possível carregar os relatórios.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const quick = days => {
    const end = new Date(); const start = new Date();
    start.setDate(end.getDate() - days + 1);
    setFilters({ start: iso(start), end: iso(end) });
  };
  const summary = data?.summary || {};
  const methodData = (data?.by_method || []).map(x => ({ name: METHOD[x.payment_method] || x.payment_method, value: Number(x.total) }));
  const methodTotal = key => { const item = (data?.by_method || []).find(x => x.payment_method === key); return item ? Number(item.total) : 0; };

  return <div>
    <div style={s.header}>
      <div><h1 style={s.title}>Relatórios</h1><p style={s.sub}>Visão financeira e operacional completa</p></div>
      <div style={s.filters}>
        <button style={s.quick} onClick={() => quick(1)}>Hoje</button>
        <button style={s.quick} onClick={() => quick(7)}>7 dias</button>
        <button style={s.quick} onClick={() => quick(30)}>30 dias</button>
        <input type="date" style={s.input} value={filters.start} onChange={e => setFilters({ ...filters, start: e.target.value })} />
        <span style={s.to}>até</span>
        <input type="date" style={s.input} value={filters.end} onChange={e => setFilters({ ...filters, end: e.target.value })} />
        <button style={s.apply} onClick={load}>Aplicar</button>
      </div>
    </div>
    {error && <div style={s.error}>{error}</div>}
    {loading ? <div style={s.loading}>Carregando relatórios...</div> : <>
      <div style={s.cards}>
        <Stat icon="💰" label="Receita total" value={money(summary.revenue)} color="#a3e635" />
        <Stat icon="⏱️" label="Horas vendidas" value={hours(summary.sold_minutes)} color="#38bdf8" />
        <Stat icon="🎟️" label="Pacotes vendidos" value={summary.package_sales || 0} color="#a78bfa" />
        <Stat icon="🖥️" label="Sessões no período" value={summary.sessions || 0} color="#f59e0b" />
        <Stat icon="🍔" label="Receita lanchonete" value={money(summary.product_revenue)} color="#10b981" />
        <Stat icon="📈" label="Margem lanchonete" value={money(summary.product_margin)} color="#34d399" />
        <Stat icon="🧾" label="Transações" value={summary.transactions || 0} color="#f472b6" />
        <Stat icon="⌛" label="Tempo médio jogado" value={hours(summary.avg_minutes)} color="#fb923c" />
        <Stat icon="💳" label="Crédito" value={money(methodTotal('card_credit'))} color="#ef4444" />
        <Stat icon="💳" label="Débito" value={money(methodTotal('card_debit'))} color="#ef4444" />
        <Stat icon="⚡" label="PIX" value={money(methodTotal('pix'))} color="#ef4444" />
        <Stat icon="💵" label="Dinheiro" value={money(methodTotal('cash'))} color="#ef4444" />
      </div>

      <div style={s.charts}>
        <Panel title="Receita por dia">
          <ResponsiveContainer width="100%" height={260}><BarChart data={data?.daily || []}>
            <XAxis dataKey="date" tickFormatter={v => new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })} stroke="#6b7280" />
            <YAxis stroke="#6b7280" /><Tooltip formatter={v => money(v)} contentStyle={s.tooltip} />
            <Bar dataKey="revenue" name="Receita" fill="#a3e635" radius={[5,5,0,0]} />
          </BarChart></ResponsiveContainer>
        </Panel>
        <Panel title="Faturamento por período">
          <ResponsiveContainer width="100%" height={260}><BarChart data={data?.by_period || []}>
            <XAxis dataKey="period" stroke="#6b7280" />
            <YAxis stroke="#6b7280" /><Tooltip formatter={v => money(v)} contentStyle={s.tooltip} />
            <Bar dataKey="revenue" name="Receita" fill="#38bdf8" radius={[5,5,0,0]} />
          </BarChart></ResponsiveContainer>
        </Panel>
        <Panel title="Tipos de pagamento">
          {methodData.length ? <ResponsiveContainer width="100%" height={260}><PieChart>
            <Pie data={methodData} dataKey="value" nameKey="name" outerRadius={90} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
              {methodData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip formatter={v => money(v)} contentStyle={s.tooltip} />
          </PieChart></ResponsiveContainer> : <Empty />}
        </Panel>
      </div>

      <div style={s.sections}>
        <Panel title="Horas vendidas por jogador">
          <Table headers={['Jogador','Horas vendidas','Total em pacotes','Pacotes']} rows={(data?.players || []).map(x => [x.nickname ? `${x.name} (${x.nickname})` : x.name, hours(x.sold_minutes), money(x.spent), x.transactions])} />
        </Panel>
        <Panel title="Pacotes de horas vendidos">
          <Table headers={['Pacote','Vendas','Horas','Receita']} rows={(data?.packages || []).map(x => [x.name, x.sales, hours(x.minutes), money(x.revenue)])} />
        </Panel>
        <Panel title="Lanchonete — produtos vendidos">
          <Table headers={['Produto','Quantidade','Receita','Margem']} rows={(data?.products || []).map(x => [x.name, x.quantity, money(x.revenue), money(x.margin)])} />
        </Panel>
        <Panel title="Receita por forma de pagamento">
          <Table headers={['Forma','Transações','Total']} rows={(data?.by_method || []).map(x => [METHOD[x.payment_method] || x.payment_method, x.transactions, money(x.total)])} />
        </Panel>
        <Panel title="Receita por tipo de venda">
          <Table headers={['Origem','Transações','Total']} rows={(data?.by_kind || []).map(x => [KIND[x.kind] || x.kind, x.transactions, money(x.total)])} />
        </Panel>
        <Panel title="Uso por máquina">
          <Table headers={['Máquina','Sessões','Tempo utilizado']} rows={(data?.machines || []).map(x => [x.name, x.sessions, hours(x.minutes)])} />
        </Panel>
        <Panel title="Jogos mais jogados">
          <Table headers={['Jogo','Jogadas','Jogadores','Equipamentos']} rows={(data?.top_games || []).map(x => [x.name, x.plays, x.players, x.machines])} />
        </Panel>
      </div>
    </>}
  </div>;
}

function Stat({ icon, label, value, color }) { return <div style={{ ...s.card, borderTopColor: color }}><span style={s.icon}>{icon}</span><strong style={{ ...s.value, color }}>{value}</strong><span style={s.label}>{label}</span></div>; }
function Panel({ title, children }) { return <section style={s.panel}><h3 style={s.panelTitle}>{title}</h3>{children}</section>; }
function Empty() { return <div style={s.empty}>Sem dados no período.</div>; }
function Table({ headers, rows, pageSize = 8 }) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows]);
  const totalPages = Math.ceil(rows.length / pageSize);
  const currentRows = rows.slice((page - 1) * pageSize, page * pageSize);
  return <div style={{display:'flex',flexDirection:'column',flex:1,justifyContent:'space-between'}}>
    <div className="touch-scroll-x" style={s.tableWrap}>{rows.length ? <table style={s.table}><thead><tr>{headers.map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead><tbody>{currentRows.map((row,i) => <tr key={i}>{row.map((v,j) => <td key={j} style={s.td}>{v}</td>)}</tr>)}</tbody></table> : <Empty />}</div>
    {totalPages > 1 && <div style={s.pagination}>
      <button style={{...s.quick, opacity: page <= 1 ? 0.5 : 1}} disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Anterior</button>
      <span>Pág {page} de {totalPages}</span>
      <button style={{...s.quick, opacity: page >= totalPages ? 0.5 : 1}} disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Próxima ›</button>
    </div>}
  </div>;
}

const s = {
  header:{display:'flex',justifyContent:'space-between',alignItems:'flex-end',gap:18,flexWrap:'wrap',marginBottom:22}, title:{color:'#fff',fontSize:26,margin:0},sub:{color:'#6b7280',fontSize:14,margin:'5px 0 0'},
  filters:{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'},quick:{padding:'9px 11px',border:'1px solid #374151',borderRadius:8,background:'transparent',color:'#9ca3af',cursor:'pointer'},input:{padding:'9px 10px',border:'1px solid #374151',borderRadius:8,background:'#14141f',color:'#e5e7eb'},to:{color:'#6b7280',fontSize:12},apply:{padding:'10px 16px',border:0,borderRadius:8,background:'var(--accent)',color:'#0a0a0f',fontWeight:700,cursor:'pointer'},
  cards:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:14,marginBottom:18},card:{minHeight:112,display:'flex',flexDirection:'column',background:'#14141f',border:'1px solid #262636',borderTop:'3px solid',borderRadius:12,padding:16},icon:{fontSize:22},value:{fontSize:23,marginTop:7},label:{color:'#9ca3af',fontSize:12,marginTop:4},
  charts:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(360px,1fr))',gap:16,marginBottom:16},sections:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(460px,1fr))',gap:16},panel:{display:'flex',flexDirection:'column',background:'#14141f',border:'1px solid #262636',borderRadius:12,padding:18,minWidth:0},panelTitle:{color:'#e5e7eb',fontSize:15,margin:'0 0 15px'},tooltip:{background:'#262636',border:'none',borderRadius:8,color:'#fff'},
  tableWrap:{overflowX:'auto'},table:{width:'100%',borderCollapse:'collapse'},th:{color:'#6b7280',fontSize:11,textTransform:'uppercase',textAlign:'left',padding:'8px 10px',borderBottom:'1px solid #30303f',whiteSpace:'nowrap'},td:{color:'#e5e7eb',fontSize:13,padding:'10px',borderBottom:'1px solid #262636',whiteSpace:'nowrap'},empty:{color:'#6b7280',textAlign:'center',padding:34},loading:{color:'#9ca3af',padding:50,textAlign:'center'},error:{color:'#fecaca',background:'#7f1d1d55',padding:12,borderRadius:8,marginBottom:16},
  pagination:{display:'flex',justifyContent:'space-between',alignItems:'center',color:'#9ca3af',fontSize:12,marginTop:16}
};
